const PREFIX = "usdt:";
const CHECK_KEY = "check";

// Paying a purchase with USDT: the deposits credited after `sinceDepositId`
// (the newest one when the user started) count toward `targetAmount` (the
// shortfall then). A pay panel is remounted whenever it returns from the
// policy Help panel, so this lives in storage. Keys: `purchase:{id}`,
// `purchase_all`.
export type UsdtDepositSession = {
  sinceDepositId: number;
  targetAmount: number;
};

// The running payment check for the user's address, so the button state and
// the faster polling survive a remount. ISO 8601 instants.
export type UsdtCheckWindow = {
  checkUntil?: string;
  retryAt?: string;
};

// Storage can be unavailable (private mode, blocked site data); everything
// here is a convenience the flows recover from without it.
const read = (key: string): unknown => {
  try {
    const raw = window.localStorage.getItem(`${PREFIX}${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

const write = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(`${PREFIX}${key}`, JSON.stringify(value));
  } catch (_error) {
    // Not persisted; the flow still works for this mount.
  }
};

const remove = (key: string) => {
  try {
    window.localStorage.removeItem(`${PREFIX}${key}`);
  } catch (_error) {
    // Nothing to clean up then.
  }
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const saveUsdtDepositSession = (
  key: string,
  value: UsdtDepositSession,
) => write(key, value);

export const getUsdtDepositSession = (
  key: string,
): UsdtDepositSession | null => {
  const parsed = read(key) as Partial<UsdtDepositSession> | null;
  return isFiniteNumber(parsed?.sinceDepositId) &&
    isFiniteNumber(parsed?.targetAmount)
    ? {
        sinceDepositId: parsed.sinceDepositId,
        targetAmount: parsed.targetAmount,
      }
    : null;
};

export const clearUsdtDepositSession = (key: string) => remove(key);

const readInstant = (value: unknown): string | undefined =>
  typeof value === "string" && !Number.isNaN(Date.parse(value))
    ? value
    : undefined;

export const getUsdtCheckWindow = (): UsdtCheckWindow => {
  const parsed = read(CHECK_KEY) as UsdtCheckWindow | null;
  return {
    checkUntil: readInstant(parsed?.checkUntil),
    retryAt: readInstant(parsed?.retryAt),
  };
};

export const saveUsdtCheckWindow = (value: UsdtCheckWindow) =>
  write(CHECK_KEY, value);
